import { ApiEntity } from '@backstage/catalog-model';
import { GoogleAnalytics4 } from '@backstage-community/plugin-analytics-module-ga4';
import { sanitizeAsyncApiDefinition } from '@internal/ui-common';
import { techRadarApiRef } from '@backstage-community/plugin-tech-radar';
import { TechRadarZF } from './lib/techradar';

import {
  analyticsApiRef,
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import {
  apiDocsConfigRef,
  defaultDefinitionWidgets,
} from '@backstage/plugin-api-docs';
import {
  ScmAuth,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import {
  ScorecardsApiClient,
  scorecardsApiRef,
} from '@internal/backstage-plugin-zf-scorecards-frontend';

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scorecardsApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      new ScorecardsApiClient(discoveryApi, fetchApi),
  }),

  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),

  ScmAuth.createDefaultApiFactory(),

  createApiFactory({
    api: analyticsApiRef,
    deps: { configApi: configApiRef, identityApi: identityApiRef },
    factory: ({ configApi, identityApi }) =>
      GoogleAnalytics4.fromConfig(configApi, {
        identityApi,
      }),
  }),

  createApiFactory(techRadarApiRef, new TechRadarZF()),

  /**
   * Overrides the api-docs plugin's own `apiDocsConfigRef` factory so that AsyncAPI definitions
   * are sanitized before the viewer validates them. Our published specs contain entries that the
   * bundler hoisted into `components/schemas` regardless of their kind, which makes
   * `@asyncapi/parser` reject the whole document and render a list of validation errors instead
   * of the docs. Every other API type keeps the default widget untouched.
   */
  createApiFactory({
    api: apiDocsConfigRef,
    deps: {},
    factory: () => {
      const definitionWidgets = defaultDefinitionWidgets().map(widget =>
        widget.type === 'asyncapi'
          ? {
              ...widget,
              component: (definition: string) =>
                widget.component(sanitizeAsyncApiDefinition(definition)),
            }
          : widget,
      );
      return {
        getApiDefinitionWidget: (apiEntity: ApiEntity) =>
          definitionWidgets.find(widget => widget.type === apiEntity.spec.type),
      };
    },
  }),
];
